import {
  Badge,
  BoxV2 as Box,
  ButtonV2 as Button,
  Drawer,
  Flex,
} from 'portal-commons';
import { FC, useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  faBan,
  faCircleCheck,
  faSquareExclamation,
  faSquareMinus,
} from '@fortawesome/pro-regular-svg-icons';

import { getBadgeTypeForMnoStatus } from '../../utils';
import {
  BrandDetailsSection,
  CarrierStatusSection,
  ConnectivityChainSection,
  CspDetailsSection,
  Divider,
  LoaSection,
  MessageProgramDetailsSection,
  MultimediaUploadSection,
  ProgramAttributesSection,
  SampleMessagesSection,
  SenderIdSection,
  SummarySection,
  TargetNetworksSection,
  Title,
} from '../../components/CampaignDetailsHU';
import CampaignComplaintFormHU from '../../components/CampaignComplaintFormHU';
import CampaignRejectionFormHU from '../../components/CampaignRejectionFormHU';
import {
  campaignController,
  campaignSigningDocumentController,
  senderIdInventoryController,
  CampaignDetail,
  CampaignLoa,
  CampaignSharingChain,
  SenderId,
} from '@/apis/core';
import { LoaPreviewModal, PageContainer } from '@/components';
import { CampaignStatus, MnoStatus } from '@/constants/enum';
import { Loader } from '@/shared_elements';
import { toastFlashMessage } from '@/utils';
import { humanizeMnoStatus } from '@/utils/string';
import { createPortal } from 'react-dom';

interface RouteParams {
  id: string;
}

enum DrawerType {
  None = 'none',
  Rejection = 'rejection',
  Complaint = 'complaint',
}

const CampaignDetailsHU: FC = () => {
  const { id } = useParams<RouteParams>();
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [campaign, setCampaign] = useState<CampaignDetail>();
  const [loas, setLoas] = useState<CampaignLoa[]>([]);
  const [senderIds, setSenderIds] = useState<SenderId[]>([]);
  const [sharingChain, setSharingChain] = useState<CampaignSharingChain>();
  const [drawerType, setDrawerType] = useState(DrawerType.None);
  const [previewDocUid, setPreviewDocUid] = useState('');
  const operationStatusType = getBadgeTypeForMnoStatus(
    campaign?.operationStatus
  );
  const isExpired = campaign?.status === CampaignStatus.Expired;

  const fetchCampaign = async (campaignUid: string) => {
    setLoading(true);
    const response = await campaignController.getCampaign(campaignUid);
    setCampaign(response ?? undefined);
    setLoading(false);
  };

  const fetchLoas = async (campaignUid: string) => {
    const response = await campaignSigningDocumentController.getLoas(
      campaignUid
    );
    setLoas(response);
  };

  const fetchSenderId = async (campaignUid: string) => {
    const response = await senderIdInventoryController.getCampaignSenderIds(
      campaignUid
    );
    setSenderIds(response);
  };

  const fetchSharingChain = async (campaignUid: string) => {
    const response = await campaignController.getSharingChain(campaignUid);
    setSharingChain(response ?? undefined);
  };

  const handleRejectionClick = () => {
    setDrawerType(DrawerType.Rejection);
  };

  const handleRejectionApply = async (explanation: string) => {
    setUpdating(true);
    const response = await campaignController.updateCampaignStatus(id, {
      status: MnoStatus.Rejected,
      explanation,
    });
    if (response) {
      toastFlashMessage(`${id} rejected successfully`, 'success');
      setDrawerType(DrawerType.None);
      fetchCampaign(id);
    }
    setUpdating(false);
  };

  const handleComplaintClick = () => {
    setDrawerType(DrawerType.Complaint);
  };

  const handleComplaintApply = async (explanation: string) => {
    setUpdating(true);
    const response = await campaignController.fileComplaint(id, {
      details: explanation,
    });
    if (response) {
      toastFlashMessage('Complaint filed successfully', 'success');
      setDrawerType(DrawerType.None);
    }
    setUpdating(false);
  };

  const handleApprovalClick = async () => {
    setUpdating(true);
    const response = await campaignController.updateCampaignStatus(id, {
      status: MnoStatus.Registered,
      explanation: 'Campaign approved',
    });
    if (response) {
      toastFlashMessage(`${id} approved successfully`, 'success');
      fetchCampaign(id);
    }
    setUpdating(false);
  };

  const handleDrawerClose = () => {
    setDrawerType(DrawerType.None);
  };

  const handleLoaPreview = useCallback(() => {
    if (loas.length < 1) {
      return;
    }

    const { docUid } = loas[0];
    setPreviewDocUid(docUid);
  }, [loas]);

  useEffect(() => {
    fetchCampaign(id);
    fetchLoas(id);
    fetchSenderId(id);
    fetchSharingChain(id);
  }, [id]);

  if (loading || !campaign) {
    return <Loader />;
  }

  return (
    <PageContainer sx={{ paddingX: '96px', paddingY: '40px', rowGap: null }}>
      <Flex sx={{ justifyContent: 'space-between', width: '100%' }}>
        {campaign.operationStatus === MnoStatus.Review ? (
          <Title note={`SMS Program ID: ${id}`}>Review Messaging Program</Title>
        ) : (
          <Flex sx={{ alignItems: 'center', gap: 'xl' }}>
            <Title>SMS Program ID: {id}</Title>
            <Badge
              size="xl"
              type={operationStatusType}
              startAdornment={
                campaign.operationStatus === MnoStatus.Registered
                  ? faCircleCheck
                  : campaign.operationStatus === MnoStatus.Rejected
                  ? faSquareMinus
                  : undefined
              }
            >
              {humanizeMnoStatus(campaign.operationStatus)}
            </Badge>
          </Flex>
        )}

        <Flex sx={{ gap: 'l' }}>
          {campaign.operationStatus === MnoStatus.Review && (
            <Button
              shape="square"
              variant="outline"
              size="lg"
              color="secondary"
              disabled={updating || isExpired}
              onClick={handleRejectionClick}
              startAdornment={faBan}
            >
              Reject Program
            </Button>
          )}
          {(campaign.operationStatus === MnoStatus.Rejected ||
            campaign.operationStatus === MnoStatus.Registered ||
            campaign.operationStatus === MnoStatus.Suspended) && (
            <Button
              shape="square"
              variant="outline"
              size="lg"
              color="secondary"
              disabled={updating || isExpired}
              onClick={handleComplaintClick}
              startAdornment={faSquareExclamation}
            >
              File a Complaint
            </Button>
          )}
          {(campaign.operationStatus === MnoStatus.Review ||
            campaign.operationStatus === MnoStatus.Rejected) && (
            <Button
              shape="square"
              size="lg"
              disabled={updating || isExpired}
              onClick={handleApprovalClick}
              startAdornment={faCircleCheck}
            >
              Approve Program
            </Button>
          )}
        </Flex>
      </Flex>

      <Flex
        sx={{
          flexDirection: 'column',
          gap: '24px',
          width: '100%',
          marginTop: '24px',
        }}
      >
        {loas.length > 0 && (
          <LoaSection
            fileName="Standard_LOA.pdf"
            onPreview={handleLoaPreview}
          />
        )}
        <SummarySection campaign={campaign} />
        <Divider />
        <SenderIdSection campaign={campaign} senderId={senderIds[0]} />
        <Divider />
        <Flex sx={{ gap: '24px' }}>
          <Box sx={{ width: '560px' }}>
            <CarrierStatusSection campaign={campaign} />
          </Box>
          <Flex
            sx={{
              flex: 1,
              flexDirection: 'column',
              gap: '24px',
              width: '100%',
            }}
          >
            <TargetNetworksSection campaign={campaign} />
            {sharingChain && (
              <ConnectivityChainSection
                campaign={campaign}
                sharingChain={sharingChain}
              />
            )}
          </Flex>
        </Flex>
        <Divider />
        <CspDetailsSection campaign={campaign} />
        <Divider />
        <BrandDetailsSection campaign={campaign} />
        <Divider />
        <Flex
          sx={{
            gap: '24px',
            alignItems: 'flex-start',
            flexDirection: ['column', 'column', 'row'],
          }}
        >
          <Box sx={{ flex: 1 }}>
            <MessageProgramDetailsSection campaign={campaign} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <MultimediaUploadSection campaign={campaign} />
          </Box>
        </Flex>
        <Divider />
        <SampleMessagesSection campaign={campaign} />
        <Divider />
        <ProgramAttributesSection campaign={campaign} />
      </Flex>

      <Drawer open={drawerType !== DrawerType.None} onClose={handleDrawerClose}>
        {drawerType === DrawerType.Rejection && (
          <CampaignRejectionFormHU
            campaign={campaign}
            onCancel={handleDrawerClose}
            onSubmit={handleRejectionApply}
          />
        )}
        {drawerType === DrawerType.Complaint && (
          <CampaignComplaintFormHU
            campaign={campaign}
            onCancel={handleDrawerClose}
            onSubmit={handleComplaintApply}
          />
        )}
      </Drawer>

      {previewDocUid &&
        createPortal(
          <LoaPreviewModal
            campaignUid={id}
            docUid={previewDocUid}
            onClose={() => setPreviewDocUid('')}
          />,
          document.body
        )}
    </PageContainer>
  );
};

export default CampaignDetailsHU;
